import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Spinner } from 'flowbite-react';
import toast from 'react-hot-toast';
import {
  getGuestInterest,
  getJoinAmount,
  getPublicChit,
  refreshAuthSession,
  setGuestInterest,
  startJoinChit,
} from '../../api/guest';
import { getApiErrorMessage } from '../../utils/landingForm';
import {
  getSessionRole,
  handoffToApp,
  isGuestLoggedIn,
  refreshLandingSession,
} from '../../utils/landingAuth';
import { useGuestSession } from '../../utils/useGuestSession';
import { getChitCardImage } from '../../utils/chitImage';
import {
  clearPendingGuestAction,
  getPendingGuestAction,
  setPendingGuestAction,
} from '../../utils/pendingGuestAction';
import GuestOtpModal from '../../Components/GuestOtp/GuestOtpModal';
import PaymentSetupSupportModal, {
  isPaymentSetupError,
} from '../../Components/GuestOtp/PaymentSetupSupportModal';
import siteUrls from '../../api-config/ApiConfig';
import {
  completePaymentCheckout,
  extractPaymentInitResult,
  isPaymentSetupCheckoutError,
} from '../../utils/paymentCheckout';

function formatInr(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  });
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

const GuestChitDetailPage = ({ theme }) => {
  const dark = theme === 'dark';
  const { chitId } = useParams();
  const navigate = useNavigate();
  const { loggedIn: guestIn, mobile: guestMobile, refresh: refreshGuestSession } =
    useGuestSession();

  const [detail, setDetail] = useState(null);
  const [joinAmount, setJoinAmount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interestStatus, setInterestStatus] = useState(null);
  const [interestBusy, setInterestBusy] = useState(false);
  const [joinBusy, setJoinBusy] = useState(false);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffMsg, setHandoffMsg] = useState(
    'Payment successful. Preparing your dashboard…',
  );
  const [otpOpen, setOtpOpen] = useState(false);
  const [paymentSupportOpen, setPaymentSupportOpen] = useState(false);
  const [paymentSupportMsg, setPaymentSupportMsg] = useState('');
  /** While resuming Pay & join after OTP, do not hit guestInterest. */
  const skipInterestFetchRef = useRef(false);

  const chit = detail?.chits || detail;

  const loadInterest = useCallback(async () => {
    if (!chitId || !isGuestLoggedIn()) {
      setInterestStatus(null);
      return;
    }
    try {
      const res = await getGuestInterest(chitId);
      if (res?.status < 400) {
        setInterestStatus(res?.data?.result?.status || null);
      }
    } catch {
      setInterestStatus(null);
    }
  }, [chitId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chitRes, amountRes] = await Promise.all([
        getPublicChit(chitId),
        getJoinAmount(chitId),
      ]);
      if (chitRes?.status >= 400) {
        toast.error(getApiErrorMessage(chitRes, 'Chit not found'));
        setDetail(null);
        return;
      }
      setDetail(chitRes?.data?.result || null);
      if (amountRes?.status < 400) {
        setJoinAmount(amountRes?.data?.result);
      }
      await loadInterest();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load chit'));
    } finally {
      setLoading(false);
    }
  }, [chitId, loadInterest]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!guestIn) {
      setInterestStatus(null);
      return;
    }
    // OTP open / pending action: resumePending owns the next API (interest POST or join).
    if (otpOpen || skipInterestFetchRef.current) return;
    const pending = getPendingGuestAction();
    if (pending?.chitId === chitId) return;
    void loadInterest();
  }, [guestIn, loadInterest, otpOpen, chitId]);

  const requireGuest = (action) => {
    if (!isGuestLoggedIn()) {
      setPendingGuestAction(action);
      setOtpOpen(true);
      return false;
    }
    return true;
  };

  const runInterest = async (status) => {
    if (!requireGuest({ type: 'interest', chitId, status })) return;
    setInterestBusy(true);
    try {
      const res = await setGuestInterest(chitId, status);
      if (res?.status >= 400) {
        toast.error(getApiErrorMessage(res, 'Could not save interest'));
        return;
      }
      // UI already reflects interest via button label — skip success toast.
      setInterestStatus(status);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save interest'));
    } finally {
      setInterestBusy(false);
    }
  };

  const openPaymentSupport = (message) => {
    setPaymentSupportMsg(
      message ||
        'Online payments are not set up for this company yet.',
    );
    setPaymentSupportOpen(true);
  };

  const runJoin = async () => {
    if (!requireGuest({ type: 'join', chitId })) return;

    // Do NOT open the app tab yet — Razorpay must open on this page first.
    setJoinBusy(true);
    try {
      const res = await startJoinChit(chitId, 'razorpay');
      if (res?.status >= 400) {
        const msg = getApiErrorMessage(res, 'Could not start join');
        const code = res?.data?.code;
        if (isPaymentSetupError({ code, message: msg })) {
          openPaymentSupport(msg);
          return;
        }
        toast.error(msg);
        return;
      }

      const result = extractPaymentInitResult(res);
      if (!result?.razorpayOrderId && !result?.paymentLink && !result?.url) {
        const msg =
          res?.data?.msg && !/initiat/i.test(String(res.data.msg))
            ? res.data.msg
            : 'Checkout details missing. Please try again.';
        if (
          isPaymentSetupError({
            code: res?.data?.code,
            message: msg,
          })
        ) {
          openPaymentSupport(msg);
          return;
        }
        toast.error(msg);
        return;
      }

      await completePaymentCheckout(result, {
        name: chit?.chitName || 'ChitBid',
        description: 'Join chit payment',
        verifyUrl: siteUrls.guest.razorpayVerify,
        getAuthHeader: () => {
          const token = localStorage.getItem('access-token');
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
        onVerifying: () => {
          setJoinBusy(false);
          setHandoffMsg('Payment received. Verifying with the server…');
          setHandoffBusy(true);
        },
      });

      setHandoffMsg('Verified. Upgrading your account to Customer…');
      setHandoffBusy(true);
      setJoinBusy(false);

      const refreshed = await refreshLandingSession(refreshAuthSession, {
        expectRole: 'CUSTOMER',
        attempts: 12,
        delayMs: 700,
      });
      if (!refreshed.ok || refreshed.role !== 'CUSTOMER') {
        setHandoffBusy(false);
        toast.error(
          refreshed.message ||
            'Payment succeeded, but your Customer login is not ready yet. Open the app and sign in with this mobile.',
        );
        navigate('/join');
        return;
      }

      if (getSessionRole() !== 'CUSTOMER') {
        setHandoffBusy(false);
        toast.error(
          'Payment succeeded, but Customer tokens are not ready. Please sign in on the app with this mobile.',
        );
        navigate('/join');
        return;
      }

      setHandoffMsg('Opening your customer dashboard…');
      // Same-tab after success (matches previous UX). Opening a tab after
      // Razorpay's async callback is often blocked and felt broken.
      handoffToApp({ target: '/dashboard', sameTab: true });
    } catch (err) {
      setHandoffBusy(false);
      const msg = err?.message || getApiErrorMessage(err, 'Could not start join');
      if (/cancelled/i.test(msg)) return;
      if (isPaymentSetupError({ message: msg }) || isPaymentSetupCheckoutError(msg)) {
        openPaymentSupport(msg);
        return;
      }
      toast.error(msg);
    } finally {
      setJoinBusy(false);
    }
  };

  const resumePending = async () => {
    const pending = getPendingGuestAction();
    clearPendingGuestAction();

    // Pay & join after OTP → only join/payment APIs (never guestInterest write/fetch).
    if (pending?.type === 'join' && pending.chitId === chitId) {
      skipInterestFetchRef.current = true;
      setOtpOpen(false);
      try {
        await runJoin();
      } finally {
        skipInterestFetchRef.current = false;
      }
      return;
    }

    setOtpOpen(false);

    // Interested / Not for me after OTP → interest API only.
    if (pending?.type === 'interest' && pending.chitId === chitId && pending.status) {
      await runInterest(pending.status);
      return;
    }

    if (isGuestLoggedIn()) {
      await loadInterest();
    }
  };

  const members = Array.isArray(chit?.membersList)
    ? chit.membersList.length
    : chit?.presentMemberCount ?? '—';

  return (
    <div
      className={`min-h-screen font-poppins ${
        dark
          ? 'bg-[#170603] bg-bannerBg bg-center bg-cover'
          : 'bg-[#FDE6E4]'
      }`}
    >
      <div className="pt-[128px] md:pt-[140px] pb-16 px-[5%] xl:px-[8%] max-w-4xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/join')}
          className={`text-sm mb-4 underline ${
            dark ? 'text-[#E3E5ED] hover:text-white' : 'text-[#666666]'
          }`}
        >
          ← All chits
        </button>

        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" color="failure" />
          </div>
        ) : !chit ? (
          <p className={dark ? 'text-white' : 'text-black'}>Chit not found.</p>
        ) : (
          <div
            className={`rounded-2xl p-5 md:p-8 shadow-md ${
              dark
                ? 'bg-[#FEFBFA] text-[#333333]'
                : 'bg-white text-[#333333] border border-[#3333334f]'
            }`}
          >
            <div className="flex gap-4 items-start mb-6">
              <img
                src={getChitCardImage(chit)}
                alt=""
                className="w-16 h-16 rounded-full object-cover bg-[#FBD4CE]"
              />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-black">
                  {chit.chitName || 'Chit'}
                </h1>
                <p className="text-sm text-[#888888]">
                  {chit.chit_id}
                  {chit.status ? ` · ${chit.status}` : ''}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-sm">
              <div>
                <p className="text-[#888888]">Chit value</p>
                <p className="font-semibold text-lg text-black">
                  {formatInr(chit.oneInstalmentTotalAmount)}
                </p>
              </div>
              <div>
                <p className="text-[#888888]">Instalment</p>
                <p className="font-semibold text-lg text-black">
                  {formatInr(chit.oneInstalment)}
                </p>
              </div>
              <div>
                <p className="text-[#888888]">Members</p>
                <p className="font-semibold text-lg text-black">
                  {members}/{chit.membersCount || '—'}
                </p>
              </div>
              <div>
                <p className="text-[#888888]">Start</p>
                <p className="font-semibold text-lg text-black">
                  {formatDate(chit.startDate)}
                </p>
              </div>
            </div>

            <div className="rounded-xl p-4 mb-6 bg-[#FBD4CE]/80">
              <h2 className="font-semibold text-lg mb-1 text-black">
                Interested in this plan?
              </h2>
              <p className="text-sm mb-4 text-[#333333]">
                Let the company know — no payment needed. They can call you back.
                Or skip ahead and join when you&apos;re ready.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  color="failure"
                  className="bg-primaryColor hover:bg-[#c74132e6]"
                  disabled={interestBusy || interestStatus === 'interested'}
                  onClick={() => runInterest('interested')}
                >
                  {interestStatus === 'interested'
                    ? 'Interest sent ✓'
                    : "I'm interested"}
                </Button>
                <Button
                  color="light"
                  className="border border-[#3333334f]"
                  disabled={interestBusy || interestStatus === 'not_interested'}
                  onClick={() => runInterest('not_interested')}
                >
                  {interestStatus === 'not_interested'
                    ? 'Passed for now ✓'
                    : 'Not for me'}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div>
                <p className="text-sm text-[#888888]">Amount to join</p>
                <p className="text-xl font-bold text-primaryColor">
                  {formatInr(joinAmount ?? chit.oneInstalment)}
                </p>
              </div>
              <Button
                className="bg-[#13224D] hover:bg-[#1a2d5c] text-white"
                disabled={joinBusy || handoffBusy}
                onClick={runJoin}
              >
                {handoffBusy
                  ? 'Opening dashboard…'
                  : joinBusy
                    ? 'Starting…'
                    : 'Pay & join'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {handoffBusy ? (
        <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/50 px-6">
          <div className="rounded-2xl bg-white px-8 py-7 text-center shadow-lg max-w-sm w-full">
            <Spinner size="lg" color="failure" className="mx-auto" />
            <p className="mt-4 text-lg font-semibold text-[#13224D]">
              Payment successful
            </p>
            <p className="mt-2 text-sm text-[#555555]">{handoffMsg}</p>
            <p className="mt-3 text-xs text-[#888888]">
              Please wait — do not close this tab.
            </p>
          </div>
        </div>
      ) : null}

      <GuestOtpModal
        isOpen={otpOpen}
        onClose={() => {
          setOtpOpen(false);
          clearPendingGuestAction();
        }}
        onGuestReady={() => {
          refreshGuestSession();
          void resumePending();
        }}
      />

      <PaymentSetupSupportModal
        isOpen={paymentSupportOpen}
        onClose={() => setPaymentSupportOpen(false)}
        message={paymentSupportMsg}
        company={chit?.company}
        chitName={chit?.chitName}
        chitId={chit?.chit_id || chitId}
        guestMobile={guestMobile}
      />
    </div>
  );
};

export default GuestChitDetailPage;
